import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useEditableContext } from 'hb-react/frontoffice/apps/Forum/contexts/EditableContext';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { type FormValues } from 'hb-react/frontoffice/apps/Forum/types';
import { NotifyPeopleTriggerButton } from 'hb-react/frontoffice/components/NotifyPeopleModal/NotifyPeopleTriggerButton';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { AIFormWidget } from 'hb-react/shared/components/AIFormWidget';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { isAnEmptyHtmlContent } from 'helpers/isAnEmptyHtmlContent';

interface CreateEditButtonsProps {
  isUploading: boolean;
  showUserTargetingButton: boolean;
}

const CreateEditButtons = ({
  isUploading,
  showUserTargetingButton,
}: CreateEditButtonsProps) => {
  const { flagEvAiFormAssistantOnForums } = useFeatureFlag();
  const { appPageConfig } = useForumContext();
  const { currentUser } = useDefaultAppContexts();
  const { contentEditableRef } = useEditableContext();
  const { setFieldValue, values, isValid } = useFormikContext<FormValues>();
  const { topicId } = useTopicContext();
  const navigate = useNavigate();

  const maxTargetedUsers = currentUser?.user_targeting?.max_targeted_users;

  const isEmptyContent =
    !values.content &&
    // @ts-expect-error quill typing
    isAnEmptyHtmlContent(contentEditableRef?.current?.getHtml() || '');
  const isEmptyTitle = !values.title;
  const isCategoryFieldMandatory =
    appPageConfig?.config?.set_category_field_mandatory;
  const isEmptyCategories = isCategoryFieldMandatory
    ? values.category_ids.length === 0
    : false;

  const isDisabledSubmit =
    !isValid || isEmptyContent || isEmptyTitle || isEmptyCategories;

  return (
    <Box
      width="100%"
      display="flex"
      textAlign="right"
      justifyContent="flex-end"
      mt={6}
      gridColumnGap={3}
    >
      {showUserTargetingButton ? (
        <Box>
          <UserTargeting
            renderTriggerElement={(onClick) => (
              <NotifyPeopleTriggerButton values={values} onClick={onClick} />
            )}
            openByClickOn={
              <Button
                aria-hidden="true"
                as="span"
                dataTestId="user-targeting-button"
                icon={() => (
                  <FontAwesomeIcon
                    iconName="bell"
                    height="16px"
                    iconType={CATEGORIES.SOLID}
                  />
                )}
                variant="secondary"
              >
                {__('Notify people')}
                {values.targeted_user_ids &&
                  values.targeted_user_ids.length > 0 &&
                  ` · ${values.targeted_user_ids.length}`}
              </Button>
            }
            defaultQuery={{
              targeting_type: 'Posts::ForumPost',
              topic_ids: [topicId],
            }}
            maxTargetedUsers={maxTargetedUsers}
            onValidateSelection={(_selectedUsers, selectedUserIds) => {
              setFieldValue('targeted_user_ids', selectedUserIds);
            }}
            onCancelSelection={() => setFieldValue('targeted_user_ids', [])}
          />
        </Box>
      ) : (
        <Button variant="secondary" onClick={() => navigate('..')}>
          {__('Cancel')}
        </Button>
      )}
      {flagEvAiFormAssistantOnForums && (
        <AIFormWidget
          mode="button"
          enabledFields={[
            {
              field: 'title',
              type: 'text',
              label: s__('Forum|Subject'),
            },
            {
              field: 'content',
              type: 'html',
              label: s__('Forum|Content'),
            },
          ]}
          placeholder={s__(
            "AIForm|What's on your mind? Share your thoughts and get the conversation started. AI can generate complete posts from a short description, translate content to other languages, or improve existing text.",
          )}
          smartSuggestions={[
            {
              title: s__('AIForm|Start discussion'),
              prompt: s__(
                'AIForm|Create an engaging forum post about [topic]. Ask thoughtful questions to encourage community discussion.',
              ),
            },
            {
              title: s__('AIForm|Share experience'),
              prompt: s__(
                'AIForm|Write a personal story about [experience]. Make it relatable and encourage others to share their experiences too.',
              ),
            },
            {
              title: s__('AIForm|Ask for help'),
              prompt: s__(
                "AIForm|Write a helpful question about [topic]. Be specific about what you need help with and what you've already tried.",
              ),
            },
            {
              title: s__('AIForm|Share knowledge'),
              prompt: s__(
                'AIForm|Write an informative post sharing your knowledge about [topic]. Include practical tips and examples.',
              ),
            },
            {
              title: s__('AIForm|Get feedback'),
              prompt: s__(
                "AIForm|Create a post asking for feedback on [idea/project]. Be specific about what you want feedback on and what you're looking for.",
              ),
            },
          ]}
        />
      )}
      <Box>
        <Button
          isDisabled={isDisabledSubmit}
          isLoading={isUploading}
          type="submit"
        >
          {s__('Forum|Publish')}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateEditButtons;
